<template>
  <div class="content-tags">
    <ul v-if="searchStore.tags.length" class="tag-actions">
      <li v-for="tag in searchStore.tags" :key="tag.id" class="action">
        <icon-button
          type="button"
          :icon="tagIcon(tag)"
          :classes="tagClasses(tag)"
          :label="tag.tagLabel"
          :aria-label="`Remove ${tag.tagLabel}`"
          :reduce="false"
          :icons-outlined="false"
          @click-action="clearItem(tag)"
        />
      </li>
    </ul>
    <ul v-if="searchStore.tags.length" class="tag-actions">
      <li v-if="showFilterCollections && userAuthStore.features.FILTER_COLLECTIONS" class="action">
        <filter-collection-form-button entity-type="workout" />
      </li>
      <li class="action clear">
        <icon-button
          type="button"
          icon="close"
          classes="clear"
          label="Clear All"
          :reduce="false"
          :icons-outlined="false"
          @click-action="clearAll"
        />
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
/**
 * Manage tags displayed on the search results tag bar.
 */
import { useUserAuthStore } from '@/stores/user-auth'
import { useSearchStore } from '@/stores/search'
import type { SearchTag } from '@/types/search-types'
import IconButton from '@/components/buttons/icon-button.vue'
import FilterCollectionFormButton from '@/components/buttons/filter-collections/filter-collection-form-button.vue'

/** PROPS **/
withDefaults(
  defineProps<{
    showFilterCollections?: boolean
  }>(),
  {
    showFilterCollections: false,
  },
)

/** STATE **/
const userAuthStore = useUserAuthStore()
const searchStore = useSearchStore()

/** EMITS **/
const emit = defineEmits(['tag-update', 'clear-all'])

/** METHODS **/
function clearAll() {
  searchStore.clearAllControls()
  emit('clear-all')
}

function tagClasses(tag: SearchTag): string {
  if (tag.type === 'checkbox' && tag.state === 'exclude') {
    return 'exclude'
  }

  return 'include'
}

function tagIcon(tag: SearchTag): string {
  if (tag.type === 'checkbox' && tag.state === 'exclude') {
    return 'disabled_by_default'
  }

  return 'check_box'
}

function clearItem(tag: SearchTag) {
  searchStore.removeTag(tag)

  // type specific side effects
  if (tag.type === 'keywords') {
    searchStore.clearKeywords()
  }

  if (tag.type === 'range') {
    searchStore.clearRange(tag)
  }

  if (tag.type === 'dropdown') {
    searchStore.clearDropdown(tag)
  }

  if (tag.type === 'checkbox') {
    searchStore.clearCheckbox(tag)
  }

  emit('tag-update')
}
</script>
